import ModalComponent from "@general-ui/modal/modal";
import { BLAdmin, IReturnableActivity } from "types/working-model";
import ActivityLogRow from "./activity-log-row";
import WaitingIndicator from "@general-ui/waiting-indicator/waiting-indicator";
import { OptionalComponent } from "utils/optional-component";
import { forwardRef, useCallback, useState } from "react";
import Avatar from "@general-ui/avatar/avatar";
import { ACTIVITY_MODAL_ID, ParsedUserFromActivity, parseUserFromActivity } from "./utils";
import { niceDateTimeDayMonthCommaTime } from "utils/utils";
import GradientButton from "@general-ui/button/gradient-button";
import Icon, { COLORS, ICONS } from "@general-ui/icon";
import { RevertPublish } from "connection/create-event";
import { FetchError } from "connection/helpers";
import { showAlert } from "@general-ui/alert/alert-service";

interface Props {
	open: boolean;
	onClose: () => void;
	activityLog: IReturnableActivity[];
	adminMap: Map<number, BLAdmin>;
	onScrollToBottom: () => void;
	loadingMore: boolean;
	eventUuid: string;
}

const ActivityModal = forwardRef<HTMLDivElement, Props>(({ open, onClose, activityLog, adminMap, onScrollToBottom, loadingMore, eventUuid }: Props, ref) => {
	const [confirmRestore, setConfirmRestore] = useState(false);
	const [restoreItem, setRestoreItem] = useState<IReturnableActivity | undefined>();
	const [restoreUser, setRestoreUser] = useState<ParsedUserFromActivity | undefined>();
	const [restoring, setRestoring] = useState(false);

	const restore = useCallback((item: IReturnableActivity) => {
		setRestoreItem(item);
		setRestoreUser(parseUserFromActivity(item, adminMap));
		setConfirmRestore(true);
	}, [adminMap]);

	const onConfirmRestore = useCallback(() => {
		if (!eventUuid || !restoreItem?.uuid) {
			return;
		}

		setRestoring(true);
		RevertPublish(eventUuid, restoreItem?.uuid).then(() => {
			window.location.reload();
		}).catch(e => {
			console.error(e);

			if (e instanceof FetchError) {
				if (e.status === 409) {
					showAlert({
						message: "Unable to roll back",
						description: "It appears that someone is actively editing this event. Please try again later.",
						type: "error"
					});
					return;
				}

				if (e.status === 401) {
					showAlert({
						message: "Unable to roll back",
						description: "It appears that a session is currently live. Please try again when the stream ends.",
						type: "error"
					});
					return;
				}
			}

			showAlert({
				message: "Unable to roll back",
				description: "We ran into an issue rolling back this project. Please try again later.",
				type: "error"
			});
		});
	}, [eventUuid, restoreItem]);

	return (
		<>
			<ModalComponent
				open={open}
				onRequestClose={onClose}
				onBodyScrollToBottom={onScrollToBottom}
				cancellable={false}
				closeable={true}
				size={'fullscreen'}
				id={ACTIVITY_MODAL_ID}
				ref={ref}
				header={(
					<>
						<h2>Activity</h2>
						<hr className="hr" />
					</>
				)}
			>
				<div className="activity-log-in-modal">
					{activityLog.map((row, i) => (
						<ActivityLogRow
							activityLog={row}
							key={row.uuid + i}
							adminMap={adminMap}
							onClickRestore={restore}
							restoring={restoring}
							allowRevert
						/>
					))}
				</div>
				<OptionalComponent display={loadingMore}>
					<div className="loading-more">
						<WaitingIndicator />
					</div>
				</OptionalComponent>
			</ModalComponent>
			<ModalComponent
				cancellable={true}
				closeable={false}
				title={'Restore project version'}
				subtitle={'Are you sure you want to restore this project version?'}
				open={confirmRestore}
				onRequestClose={() => setConfirmRestore(false)}
				padTitle={false}
				footer={(
					<div className="restore-confirm-footer">
						<button disabled={restoring} className="small" onClick={() => setConfirmRestore(false)}>Cancel</button>
						<GradientButton
							fullBackgroundGradient
							adminButton
							width="fit-content"
							buttonProps={{
								onClick: onConfirmRestore,
								disabled: restoring
							}}
						>
							{restoring ? (
								<WaitingIndicator />
							) : <>Restore</>}
						</GradientButton>
					</div>
				)}
			>
				<div className="restore-confirm">
					{restoreItem && (
						<div className="restore-confirm-card">
							<Avatar imageUrl={restoreUser?.picture} initials={restoreUser?.initials} />
							<div className="restore-user-confirm-card-user">
								<span className="user-name">{restoreUser?.name}</span>
								<time>{niceDateTimeDayMonthCommaTime(restoreItem.timestamp_id)}</time>
							</div>
						</div>
					)}
					<p className="restore-warning"><Icon name={ICONS.EXCLAMATION_MARK} size={12} color={COLORS.YELLOW} /> All changes to your project made prior to restoring this version will be permanently lost.</p>
				</div>
			</ModalComponent>
		</>
	);
});

export default ActivityModal;